import { HttpStatusCode } from 'axios'

import { HttpService } from '@/api/shared/http.service'
import { ApiPagedPayload, ApiPagedResponse } from '@/api/shared/types'
import { migrateFromV1ToV2 } from '@/api/shared/utils'

import { SignInResponse } from '../../auth/lib/auth.types'
import {
  CreateInviteDTO,
  DetailedInvite,
  Invite,
  InvitePermission,
  InviteStatus
} from './invites.types'
import {
  Contact,
  ContactDetails,
  ContactsPayload,
  Permission,
  ProjectFeature,
  RegisterByInvitePayload,
  TemporaryTokenPayload,
  TemporaryTokenResponse,
  UserAgreementsPayload
} from './users.types'

class UsersService extends HttpService {
  async getContacts(params: ApiPagedPayload<ContactsPayload>) {
    const { data } = await this.axios.get<ApiPagedResponse<Contact[]>>(
      this.getPath('/contacts'),
      { params }
    )
    return data
  }

  async getInvites() {
    const { data } = await this.axios.get<{ data: Invite[] }>(
      this.getPath('/invites')
    )
    return data
  }

  async getInvitesPermissions() {
    const { data } = await this.axios.get<{ data: InvitePermission[] }>(
      this.getPath('/invites/permissions')
    )
    return data
  }

  async getInviteById(id: string) {
    const { data } = await this.axios.get<DetailedInvite>(
      this.getPath(`/invites/${id}`)
    )
    return data
  }

  async createInvite(params: CreateInviteDTO) {
    return this.axios.post(this.getPath('/invites'), params)
  }

  async deleteInvite(id: string) {
    return this.axios.delete(this.getPath(`/invites/${id}`))
  }

  async getInvitesStatuses() {
    const { data } = await this.axios.get<{ data: InviteStatus[] }>(
      this.getPath('/invites/statuses')
    )
    return data
  }

  async getPermissions() {
    const { data } = await this.axios.get<{ data: Permission[] }>(
      this.getPath('/me/permissions')
    )
    return data
  }

  async getContactById(id: string) {
    const { data } = await this.axios.get<ContactDetails>(
      this.getPath(`/contacts/${id}`)
    )
    return data
  }

  async getProjectFeatures(id: string) {
    const { data } = await this.axios.get<ProjectFeature>(
      this.getPath(`/projects/${id}/features`)
    )
    return data
  }

  async deleteContactById(id: string) {
    return this.axios.delete(this.getPath(`/contacts/${id}`))
  }

  async sendInviteSmsCode(phone_number: string) {
    return this.makeSafeRequest<{ code?: string }>({
      url: migrateFromV1ToV2(this.getPath('/invites/code')),
      method: 'POST',
      data: { phone_number }
    })
  }

  async getTemporaryToken({ phone_number, code }: TemporaryTokenPayload) {
    return this.makeSafeRequest<TemporaryTokenResponse>({
      url: this.getPath('/invites/token'),
      method: 'POST',
      data: { phone_number, code }
    })
  }

  async registerByInvite(
    { isLegal, firstName, lastName, title, email }: RegisterByInvitePayload,
    accessToken?: string
  ) {
    return this.makeSafeRequest<SignInResponse>({
      method: 'POST',
      url: migrateFromV1ToV2(this.getPath('/register')),
      data: isLegal
        ? { title, email, token: accessToken }
        : { firstName, lastName, email, token: accessToken }
    })
  }

  async saveAgreements(data: UserAgreementsPayload) {
    return this.makeSafeRequest(
      {
        method: 'POST',
        url: migrateFromV1ToV2(this.getPath('/agreements')),
        data
      },
      {
        okIfStatus: [
          HttpStatusCode.Ok,
          HttpStatusCode.Created,
          HttpStatusCode.NoContent
        ]
      }
    )
  }

  async repeatInvite(inviteId: string) {
    return this.axios.post(this.getPath(`/invites/${inviteId}/copy`))
  }
}

const UsersServiceSingleton = new UsersService('/api/v1/users')
export { UsersServiceSingleton as UsersService }
